/**
 * `WorkflowYaml.ts` — the YAML codec for the workflow document, and the sparse
 * patch that edits one.
 *
 * The workflow is STORED as one structured YAML document (`workflow.yaml`).
 * This module owns every text<->model conversion:
 *
 *  - `serializeWorkflowDocument` — canonical emitter. Fixed key order, no line
 *    folding, defaults omitted. Canonical output is load-bearing three times
 *    over: the review signature hashes it, the editor's echo comparison
 *    byte-compares it, and the debounced S3 upload diffs on it. An unchanged
 *    document MUST re-serialize byte-identically.
 *  - `parseWorkflowDocumentText` — full-document parse (the dev raw editor
 *    and storage load use it). STRICT about
 *    keys and enums: an unknown key or an inapplicable value is an error
 *    naming it, never silently dropped or defaulted — including nested
 *    `scripts[]`, `scripts[].steps[]`, `triggers[]`, `type`,
 *    script `trigger`/`revert`, trigger `type`, and tool `args` keys that
 *    are not parameters of that tool. Tolerant about entry shapes: a
 *    malformed scripts/steps/triggers entry is preserved verbatim
 *    (`invalidRaw`) and reported by `validate()` — a malformed entry must
 *    never read as "no data", or the next write would destroy what it still
 *    holds.
 *  - `parseWorkflowPatchText` / `applyWorkflowPatch` — the sparse edit path
 *    (`update_workflow`'s `yaml` parameter and the dev console).
 *    Absent keys mean "leave alone"; an explicit `null` clears a field.
 *    Identity keys (`uid`, `project`) and the retired `workflow_state` key
 *    are REFUSED on the way in.
 *  - `documentFromPatch` — create-from-scratch for a project with no workflow.
 *
 * Pure logic — no DOM, no Vue, no IOC.
 */

import {
  LineCounter,
  isMap as isYamlMap,
  isNode as isYamlNode,
  isScalar as isYamlScalar,
  isSeq as isYamlSequence,
  parseDocument as parseYamlDocumentNode,
  stringify as stringifyYaml,
} from "yaml";
import { getToolArgKeys } from "@ai/registry/scriptCommandCatalog";
import {
  COMPLETION_CONDITIONS,
  MEDIA_FITS,
  MEDIA_TYPES,
  SCRIPT_REVERTS,
  SCRIPT_TRIGGERS,
  STEP_TRIGGER_TYPES,
  WORKFLOW_TYPES,
  USER_INPUT_ALLOWANCES,
  normalizeUserInputAllowance,
  type WorkflowUserInputAllowance,
  WorkflowCompletionConditionType,
  WorkflowDocument,
  WorkflowGroup,
  WorkflowKnowledgeDoc,
  WorkflowPanelDock,
  WorkflowPanelPlacement,
  WorkflowPanelScreenDock,
  WorkflowUiState,
  WORKFLOW_PANEL_DOCK_ALIGNS,
  WORKFLOW_PANEL_DOCK_SIDES,
  WORKFLOW_PANEL_SCREEN_ALIGNS,
  WorkflowMediaFit,
  WorkflowMediaType,
  WorkflowScript,
  WorkflowScriptStep,
  WorkflowStep,
  WorkflowStepAttachment,
  WorkflowStepView,
  WorkflowStepCompletionCondition,
  WorkflowStepMedia,
  WorkflowStepTrigger,
  cloneWorkflowDocument,
  normalizeCompletionCondition,
  normalizeMediaFit,
  normalizeMediaType,
  normalizeNextTarget,
  normalizeScriptRevert,
  normalizeScriptTrigger,
  normalizeStepTriggerType,
  normalizeWorkflowType,
  rebuildGroupMembership,
} from "./WorkflowDocument";
import type {
  WorkflowPanelDockAlign,
  WorkflowPanelDockSide,
  WorkflowPanelScreenAlign,
} from "./WorkflowDocument";

// ---------------------------------------------------------------------------
// YAML scalar helpers (shared with the run model and the subagents)
// ---------------------------------------------------------------------------

/**
 * `lineWidth: 0` disables YAML's line folding. Without it a long verbatim
 * `say:` line wraps mid-sentence — still valid YAML, but it churns the diff on
 * every write and makes the raw dev view unreadable.
 */
const K_STRINGIFY_OPTIONS = { lineWidth: 0 } as const;

/** True when `value` is a plain object (not null, not an array). */
export function isYamlMapping(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** Reads a string field, trimming it; returns "" for anything non-string. */
export function yamlString(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

/**
 * A step `scene` value: display name, 1-based index, or scene id. YAML
 * `scene: 1` parses as a number, so this stringifies finite numbers instead
 * of dropping them the way {@link yamlString} does.
 */
export function yamlSceneRef(value: unknown): string | null {
  if (value === null || value === undefined) return null;
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  if (typeof value === "string") return value.trim();
  return "";
}

/**
 * Reads a list of strings, dropping non-strings. Accepts a bare string as a
 * one-item list — a caller writing `completed: Overview` means one name.
 */
export function yamlStringList(value: unknown): string[] {
  if (typeof value === "string") {
    const single = value.trim();
    return single.length === 0 ? [] : [single];
  }

  if (!Array.isArray(value)) return [];

  const result: string[] = [];
  for (const entry of value) {
    if (typeof entry === "string") {
      const trimmed = entry.trim();
      if (trimmed.length > 0) result.push(trimmed);
    } else if (typeof entry === "number" || typeof entry === "boolean") {
      // A bare `- 3 seconds` parses as a number; keep it as authored text.
      result.push(String(entry));
    }
  }

  return result;
}

export interface WorkflowYamlIssue {
  readonly message: string;
  /** 1-based source line, when the parser or schema path identifies one. */
  readonly line: number | null;
  /** 1-based source column, when available. */
  readonly column: number | null;
}

type WorkflowYamlPath = ReadonlyArray<string | number>;

interface ParsedYamlDocument {
  readonly value: unknown;
  readonly issues: WorkflowYamlIssue[];
  readonly locate: (path: WorkflowYamlPath) => { line: number; column: number } | null;
}

/**
 * Structured error carried across the model boundary. Its message keeps the
 * `invalid_workflow_yaml:` prefix the executor already branches on, while UI
 * callers can render and navigate the individual issues.
 */
export class InvalidWorkflowYamlError extends Error {
  readonly issues: readonly WorkflowYamlIssue[];

  constructor(issues: readonly WorkflowYamlIssue[]) {
    const message = issues
      .map((issue) => (issue.line === null ? issue.message : `Line ${issue.line}: ${issue.message}`))
      .join(" ");
    super(`invalid_workflow_yaml: ${message}`);
    this.name = "InvalidWorkflowYamlError";
    this.issues = [...issues];
  }
}

/**
 * Parses standalone YAML and preserves every syntax error reported by the YAML
 * parser. `yaml.parse()` throws only the first one; the raw editor needs the
 * complete list so its issue stepper can move through independent mistakes.
 *
 * The returned locator resolves semantic schema paths against the parsed YAML
 * AST. If a requested child is missing, it walks up to the nearest existing
 * parent, so an error such as a missing `steps[2].name` still points at that
 * step rather than losing its location.
 */
export function parseYamlDocument(text: string): ParsedYamlDocument {
  const noLocation = () => null;
  if (text.trim().length === 0) {
    return {
      value: null,
      issues: [{ message: "The YAML document is empty.", line: 1, column: 1 }],
      locate: noLocation,
    };
  }

  try {
    const lineCounter = new LineCounter();
    const document = parseYamlDocumentNode(text, {
      lineCounter,
      // We render line/column separately in the issue card; keep the message
      // itself concise instead of embedding a source excerpt into every item.
      prettyErrors: false,
    });

    const positionAt = (offset: number): { line: number; column: number } => {
      const tracked = lineCounter.linePos(offset);
      if (tracked.line > 0) return { line: tracked.line, column: tracked.col };

      // Defensive fallback for an offset before the first newline registered
      // by the parser (normally only the first line).
      const before = text.slice(0, Math.max(0, offset));
      const lines = before.split("\n");
      return { line: lines.length, column: (lines.at(-1)?.length ?? 0) + 1 };
    };

    const locate = (path: WorkflowYamlPath): { line: number; column: number } | null => {
      let node = document.contents;
      let nearest = isYamlNode(node) ? node : null;
      if (path.length === 0) {
        return nearest?.range ? positionAt(nearest.range[0]) : null;
      }

      for (let index = 0; index < path.length; index++) {
        const segment = path[index];
        const isLast = index === path.length - 1;

        if (typeof segment === "string" && isYamlMap(node)) {
          const pair = node.items.find((item) => {
            return isYamlScalar(item.key) && String(item.key.value) === segment;
          });
          if (!pair) break;

          // A mapping error names the KEY. Collection values commonly start on
          // the next line, which is exactly why `getIn(path)` marked line 120
          // for a misspelled key written on line 119.
          if (isLast && isYamlNode(pair.key) && pair.key.range) {
            return positionAt(pair.key.range[0]);
          }

          node = pair.value;
          if (isYamlNode(node)) nearest = node;
          continue;
        }

        if (typeof segment === "number" && isYamlSequence(node)) {
          const item = node.items[segment];
          if (!item) break;
          if (isLast && isYamlNode(item) && item.range) {
            return positionAt(item.range[0]);
          }
          node = item;
          if (isYamlNode(node)) nearest = node;
          continue;
        }

        break;
      }

      // Missing child (for example `steps[2].name`) points at the nearest
      // existing parent section.
      return nearest?.range ? positionAt(nearest.range[0]) : null;
    };

    const issues = document.errors.map((error) => {
      const position = positionAt(error.pos[0]);
      return {
        message: error.message,
        line: position.line,
        column: position.column,
      };
    });
    if (issues.length > 0) return { value: null, issues, locate };
    return { value: document.toJS() ?? null, issues: [], locate };
  } catch (e) {
    return {
      value: null,
      issues: [
        {
          message: e instanceof Error ? e.message : String(e),
          line: null,
          column: null,
        },
      ],
      locate: noLocation,
    };
  }
}

function pathSegments(text: string): Array<string | number> {
  const result: Array<string | number> = [];
  for (const match of text.matchAll(/([A-Za-z_][\w-]*)|\[(\d+)\]/g)) {
    result.push(match[1] ?? Number(match[2]));
  }
  return result;
}

/**
 * Turns the codec's existing path-bearing error copy into an AST path. Keeping
 * the messages as the source avoids a second validation vocabulary; this only
 * decides which source line the already-established issue belongs to.
 */
function pathForWorkflowIssue(message: string): WorkflowYamlPath | null {
  let match = /^Unknown top-level key "([^"]+)"/.exec(message);
  if (match?.[1]) return [match[1]];

  if (/^Unknown workflow type "/.test(message)) return ["type"];

  match = /^(project) has unknown key "([^"]+)"/.exec(message);
  if (match?.[1] && match[2]) return [match[1], match[2]];

  /*
    ⚠ `workflow_ui` MUST be matched before any `workflow` branch below.
    The catch-all near the end of this function has no trailing requirement, so
    its `workflow` alternative matches the PREFIX of `workflow_ui…` and every
    issue in this section would be located to the `workflow:` header line —
    marking the wrong row in the raw editor, for a section that is not even
    near it. (`'workflow_ui' must be a mapping.` and
    `Unknown top-level key "workflow_ui".` both already resolve correctly
    through other branches; it is only the dotted paths that collide.)
  */
  match =
    /^(workflow_ui(?:\.[A-Za-z_][\w-]*(?:\[\d+\])?)*) has unknown key "([^"]+)"/.exec(message);
  if (match?.[1] && match[2]) return [...pathSegments(match[1]), match[2]];

  match = /^(workflow_ui(?:\.[A-Za-z_][\w-]*(?:\[\d+\])?)*)/.exec(message);
  if (match?.[1]) return pathSegments(match[1]);

  match = /^(workflow(?:\.[A-Za-z_][\w-]*)*) has unknown key "([^"]+)"/.exec(message);
  if (match?.[1] && match[2]) return [...pathSegments(match[1]), match[2]];

  match =
    /^((?:steps|groups)\[\d+\](?:\.[A-Za-z_][\w-]*(?:\[\d+\])?)*) has unknown key "([^"]+)"/.exec(
      message,
    );
  if (match?.[1] && match[2]) return [...pathSegments(match[1]), match[2]];

  match = /^'([^']+)'/.exec(message);
  if (match?.[1]) return [match[1]];

  match =
    /^(workflow(?:\.[A-Za-z_][\w-]*)*|(?:steps|groups)(?:\[\d+\])?(?:\.[A-Za-z_][\w-]*(?:\[\d+\])?)*)/.exec(
      message,
    );
  if (match?.[1]) {
    const path = pathSegments(match[1]);
    const missingKey = /needs (?:a )?'([^']+)'/.exec(message)?.[1];
    if (missingKey) path.push(missingKey);
    const enumField = / has an? unknown (trigger|revert|type|run) "/.exec(message)?.[1];
    if (enumField) path.push(enumField);
    return path;
  }

  if (message.startsWith("The workflow document must be")) return [];
  return null;
}

function issuesForMessages(parsed: ParsedYamlDocument, messages: readonly string[]): WorkflowYamlIssue[] {
  return messages.map((message) => {
    const path = pathForWorkflowIssue(message);
    const position = path === null ? null : parsed.locate(path);
    return {
      message,
      line: position?.line ?? null,
      column: position?.column ?? null,
    };
  });
}

/** Serializes any value as YAML with the module's canonical options. */
export function stringifyYamlDocument(value: unknown): string {
  if (value === null || value === undefined) return "";
  return stringifyYaml(value, K_STRINGIFY_OPTIONS);
}

// ---------------------------------------------------------------------------
// Serialization (document -> canonical YAML text)
// ---------------------------------------------------------------------------

/**
 * Serializes the document with a FIXED key order:
 * uid, type, version, project, workflow, groups, steps, workflow_ui.
 * Absent (`null`) fields are omitted; present-but-empty
 * fields serialize as `""` so the authored view knows to render their labels.
 *
 * `workflow_ui` is LAST because it is machine-owned: a human opening the raw
 * file reads identity, then the header, then the content, and a block the app
 * wrote has no business interrupting that.
 */
export function serializeWorkflowDocument(document: WorkflowDocument): string {
  const out: Record<string, unknown> = {};
  if (document.uid.length > 0) out.uid = document.uid;
  if (document.type.length > 0) out.type = document.type;
  if (document.version.length > 0) out.version = document.version;
  if (document.projectId.length > 0 || document.projectName.length > 0) {
    const project: Record<string, unknown> = {};
    if (document.projectId.length > 0) project.id = document.projectId;
    if (document.projectName.length > 0) project.name = document.projectName;
    out.project = project;
  }

  out.workflow = serializeHeader(document);

  if (document.groups.length > 0) {
    out.groups = document.groups.map((group) => ({ name: group.name }));
  }

  out.steps = document.steps.map((step) => serializeStep(step));

  // Omitted entirely when nothing deviates from the designed arrangement —
  // which IS the "only present when the layout was modified" requirement, with
  // no dirty flag to keep in step. `isEmpty` is the single test.
  const ui = document.workflowUi;
  if (ui !== null && !ui.isEmpty) out.workflow_ui = serializeWorkflowUi(ui);

  return stringifyYamlDocument(out);
}

function serializeWorkflowUi(ui: WorkflowUiState): Record<string, unknown> {
  return {
    panels_layout: ui.panelsLayout.map((entry) => {
      const out: Record<string, unknown> = { panel: entry.panel };
      if (entry.dock !== null) {
        const dock: Record<string, unknown> = {
          parent: entry.dock.parent,
          // An unrecognised side round-trips VERBATIM rather than being dropped,
          // the rule every malformed entry in this format follows.
          side: entry.dock.side ?? entry.dock.invalidSide,
        };
        // OMITTED when unstated, so a placement whose alignment nobody chose
        // does not grow a key claiming one — and an older file that never had
        // the key round-trips byte-identically.
        const align = entry.dock.align ?? entry.dock.invalidAlign;
        if (align.length > 0) dock.align = align;
        out.dock = dock;
      }
      if (entry.screen !== null) {
        const screen: Record<string, unknown> = {};
        const x = entry.screen.x ?? (entry.screen.invalidX.length > 0 ? entry.screen.invalidX : null);
        const y = entry.screen.y ?? (entry.screen.invalidY.length > 0 ? entry.screen.invalidY : null);
        if (x !== null) screen.x = x;
        if (y !== null) screen.y = y;
        out.screen = screen;
      }
      if (entry.position !== null) out.position = { x: entry.position.x, y: entry.position.y };
      if (entry.embed !== null) out.embed = entry.embed;
      return out;
    }),
  };
}

function serializeHeader(document: WorkflowDocument): Record<string, unknown> {
  const workflow: Record<string, unknown> = { name: document.workflowName };
  if (document.description !== null) workflow.description = document.description;
  if (document.expectedDuration !== null) workflow.takes_about = document.expectedDuration;
  if (document.language !== null) workflow.language = document.language;
  if (document.product !== null) workflow.product = document.product;
  if (document.userInputAllowed !== null || document.invalidUserInputAllowed.length > 0) {
    workflow.user_input_allowed = [
      ...(document.userInputAllowed ?? []),
      ...document.invalidUserInputAllowed,
    ];
  }
  if (document.autostartDesktop !== null) workflow.autostart_desktop = document.autostartDesktop;
  if (document.autostartHeadset !== null) workflow.autostart_headset = document.autostartHeadset;
  if (document.rememberProgress !== null) workflow.remember_progress = document.rememberProgress;
  if (document.requiresConsolePlacement !== null) {
    workflow.requires_console_placement = document.requiresConsolePlacement;
  }
  if (document.lockUi !== null) workflow.lock_ui = document.lockUi;
  if (document.useCustomUi !== null) workflow.use_custom_ui = document.useCustomUi;
  if (document.disableMobileKeyboard !== null) {
    workflow.disable_mobile_keyboard = document.disableMobileKeyboard;
  }
  if (document.knowledgeDocs.length > 0) {
    workflow.knowledge = {
      include: document.knowledgeDocs.map((entry) =>
        entry.isInvalid ? entry.invalidRaw : { doc: entry.doc },
      ),
    };
  }
  return workflow;
}

/**
 * One step as its canonical serialized entry. Exported for the review layer,
 * which captures addressed values from it (stale-fix detection) and hashes it
 * for per-step change tracking.
 */
export function serializeStepEntry(step: WorkflowStep): Record<string, unknown> {
  return serializeStep(step);
}

function serializeStep(step: WorkflowStep): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (step.stepId.length > 0) out.id = step.stepId;
  out.name = step.name;
  if (step.disabled !== null) out.disabled = step.disabled;
  if (step.groupName.length > 0) out.group = step.groupName;
  if (step.scene !== null) out.scene = step.scene;
  if (step.expectedDuration !== null) out.takes_about = step.expectedDuration;
  if (step.stepContext !== null) out.step_context = step.stepContext;
  if (step.stepInstructions !== null) out.step_instructions = step.stepInstructions;
  if (step.backButton !== null) out.back_button = step.backButton;
  if (step.nextButton !== null) out.next_button = step.nextButton;
  if (step.nextTarget !== null) out.next_target = step.nextTarget;
  if (step.isCompletionStep !== null) out.is_completion_step = step.isCompletionStep;
  if (step.completionCondition !== null && !step.completionCondition.isEmpty) {
    out.completion_condition = serializeCompletionCondition(step.completionCondition);
  }
  if (step.userInputAllowed !== null || step.invalidUserInputAllowed.length > 0) {
    out.user_input_allowed = [...(step.userInputAllowed ?? []), ...step.invalidUserInputAllowed];
  }
  if (step.attachment !== null && !step.attachment.isEmpty) {
    const attachment: Record<string, unknown> = {};
    if (step.attachment.part.length > 0) attachment.part = step.attachment.part;
    if (step.attachment.point !== null) attachment.point = { ...step.attachment.point };
    out.attachment = attachment;
  }

  if (step.stepView !== null && !step.stepView.isEmpty) {
    const view: Record<string, unknown> = {};
    if (step.stepView.position !== null) view.position = { ...step.stepView.position };
    if (step.stepView.pivot !== null) view.pivot = { ...step.stepView.pivot };
    out.step_view = view;
  }

  if (step.media !== null && !step.media.isEmpty) {
    out.media = serializeMedia(step.media);
  }

  if (step.triggers.length > 0) {
    out.triggers = step.triggers.map((trigger) => serializeTrigger(trigger));
  }

  if (step.scripts.length > 0) {
    out.scripts = step.scripts.map((script) => serializeScript(script));
  }

  return out;
}

/**
 * A step's completion condition as its canonical entry. An unrecognised type
 * word round-trips verbatim so `validate()`'s error survives a rewrite, and
 * `delay_seconds` is omitted when absent rather than written as 0 — 0 is a
 * condition that opens instantly, which is not the same as one with no length.
 */
function serializeCompletionCondition(
  condition: WorkflowStepCompletionCondition,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (condition.invalidType.length > 0) out.type = condition.invalidType;
  else if (condition.type !== null) out.type = condition.type;
  if (condition.delaySeconds !== null) out.delay_seconds = condition.delaySeconds;
  return out;
}

/**
 * A step's media as its canonical entry. Every field is omitted when empty,
 * including `type` — an absent type is inferred from the URL, so writing the
 * inferred value back would put a decision in the file the author never made.
 * An unrecognised type word round-trips verbatim so `validate()`'s error
 * survives a rewrite.
 */
function serializeMedia(media: WorkflowStepMedia): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (media.invalidType.length > 0) out.type = media.invalidType;
  else if (media.type !== null) out.type = media.type;
  if (media.invalidFit.length > 0) out.fit = media.invalidFit;
  else if (media.fit !== null) out.fit = media.fit;
  if (media.url.length > 0) out.url = media.url;
  if (media.name.length > 0) out.name = media.name;
  if (media.description.length > 0) out.description = media.description;
  if (media.transcript.length > 0) out.transcript = media.transcript;
  if (media.startTime.length > 0) out.start = media.startTime;
  if (media.endTime.length > 0) out.end = media.endTime;
  // `false` is written back rather than dropped: it is the same behaviour as
  // absent, but it is a decision the author wrote down, and a canonical
  // rewrite that deleted it would edit their file for no observable gain.
  if (media.loop !== null) out.loop = media.loop;
  if (media.autoplay !== null) out.autoplay = media.autoplay;
  return out;
}

function serializeTrigger(trigger: WorkflowStepTrigger): unknown {
  // Malformed entries round-trip verbatim so validate()'s error survives a
  // rewrite instead of the entry being silently normalized or dropped.
  if (trigger.isInvalid) return trigger.invalidRaw;
  if (trigger.invalidText.length > 0) return { raw: trigger.invalidText };

  return {
    type: trigger.invalidType.length > 0 ? trigger.invalidType : trigger.type,
    label: trigger.label,
    go_to: trigger.goTo,
  };
}

function serializeScript(script: WorkflowScript): unknown {
  if (script.isInvalid) return script.invalidRaw;

  const entry: Record<string, unknown> = { id: script.id };
  if (script.name.length > 0) entry.name = script.name;

  entry.trigger = script.invalidTrigger.length > 0 ? script.invalidTrigger : script.trigger;
  if (script.invalidRevert.length > 0) {
    entry.revert = script.invalidRevert;
  } else if (script.revert !== "none") {
    entry.revert = script.revert;
  }

  entry.steps = script.steps.map((step) => {
    if (step.isInvalid) return step.invalidRaw;
    if (step.isSay) return { say: step.sayText };

    const out: Record<string, unknown> = { tool: step.tool };
    if (step.args !== null && Object.keys(step.args).length > 0) out.args = step.args;
    return out;
  });

  return entry;
}

// ---------------------------------------------------------------------------
// Full-document parse (YAML text -> WorkflowDocument)
// ---------------------------------------------------------------------------

export interface WorkflowDocumentParseResult {
  readonly document: WorkflowDocument | null;
  readonly errors: string[];
  readonly issues: WorkflowYamlIssue[];
  /** The retired shared-state key was accepted but deliberately not modeled. */
  readonly discardedLegacyWorkflowState: boolean;
}

/**
 * The accepted key sets, exported because they are the codec's own answer to
 * "what may this file contain" — `WorkflowSchemaCatalog.ts` renders the
 * agent-facing schema straight off them, so a key added here shows up in the
 * published schema without a second edit that could be forgotten.
 */
export const WORKFLOW_DOCUMENT_KEYS = [
  "uid",
  "type",
  "version",
  "project",
  "workflow",
  "groups",
  "steps",
  "workflow_ui",
];

/** The top-level machine-owned UI section. Refused by name on the patch path. */
export const WORKFLOW_UI_KEY = "workflow_ui";

/** `workflow_ui`'s own members. */
export const WORKFLOW_UI_KEYS = ["panels_layout"];

/** One `panels_layout` entry. */
/**
 * ⚠ `free` is RETIRED (2026-08-31, superseded by `position`) and stays here
 * ACCEPTED-AND-IGNORED rather than being removed.
 *
 * `rejectUnknownKeys` turns an unlisted key into a parse ERROR, which for a
 * top-level section means the whole document is unreadable — and this section is
 * app-written cosmetic state. A panel offset written by an older build must
 * never cost somebody their workflow, which is the rule `workflow_ui` already
 * carries for its VALUES; this is the same rule for a key. See
 * `WorkflowPanelPlacement.position` for why the meaning could not simply change
 * under the old name.
 */
export const WORKFLOW_PANEL_PLACEMENT_KEYS = [
  "panel",
  "dock",
  "screen",
  "position",
  "free",
  "embed",
];

/** A placement's `dock` block. */
export const WORKFLOW_PANEL_DOCK_KEYS = ["parent", "side", "align"];

/** A placement's `screen` block. */
export const WORKFLOW_PANEL_SCREEN_KEYS = ["x", "y"];

/** A placement's `free` block — viewport FRACTIONS, never pixels. */
export const WORKFLOW_PANEL_POSITION_KEYS = ["x", "y"];

export const WORKFLOW_HEADER_KEYS = [
  "name",
  "description",
  "takes_about",
  "language",
  "product",
  "knowledge",
  "user_input_allowed",
  "autostart_desktop",
  "autostart_headset",
  "remember_progress",
  "requires_console_placement",
  "lock_ui",
  "use_custom_ui",
  "disable_mobile_keyboard",
];

export const WORKFLOW_STEP_KEYS = [
  "id",
  "name",
  "disabled",
  "group",
  "scene",
  "takes_about",
  "step_context",
  "step_instructions",
  "back_button",
  "next_button",
  "next_target",
  "is_completion_step",
  "completion_condition",
  "user_input_allowed",
  "attachment",
  "step_view",
  "media",
  "triggers",
  "scripts",
];

export const WORKFLOW_TRIGGER_KEYS = ["type", "label", "go_to", "raw"];

export const WORKFLOW_SCRIPT_KEYS = ["id", "name", "trigger", "revert", "steps"];

export const WORKFLOW_SCRIPT_STEP_KEYS = ["say", "tool", "args"];

export const WORKFLOW_PROJECT_KEYS = ["id", "name"];

export const WORKFLOW_ATTACHMENT_POINT_KEYS = ["x", "y", "z"];

/** A `step_view` block: the camera and the point it looks at. */
export const WORKFLOW_STEP_VIEW_KEYS = ["position", "pivot"];

/** Both vectors of a `step_view`, same shape as an attachment point. */
export const WORKFLOW_STEP_VIEW_VECTOR_KEYS = ["x", "y", "z"];

export const WORKFLOW_COMPLETION_CONDITION_KEYS = ["type", "delay_seconds"];

export const WORKFLOW_MEDIA_KEYS = [
  "type",
  "fit",
  "url",
  "name",
  "description",
  "transcript",
  "start",
  "end",
  "loop",
  "autoplay",
];

function rejectUnknownKeys(
  mapping: Record<string, unknown>,
  allowed: readonly string[],
  where: string,
  errors: string[],
): void {
  for (const key of Object.keys(mapping)) {
    if (!allowed.includes(key)) errors.push(`${where} has unknown key "${key}".`);
  }
}

function rejectUnknownToolArgs(
  tool: string,
  args: Record<string, unknown>,
  where: string,
  errors: string[],
): void {
  const allowed = getToolArgKeys(tool);
  if (allowed === null) return;

  if (allowed.size === 0) {
    for (const key of Object.keys(args)) {
      errors.push(`${where}.args has unknown key "${key}" (${tool} takes no arguments).`);
    }
    return;
  }

  for (const key of Object.keys(args)) {
    if (!allowed.has(key)) {
      errors.push(
        `${where}.args has unknown key "${key}" (not a parameter of ${tool}; expected ${[...allowed].join(", ")}).`,
      );
    }
  }
}

/**
 * Parses a full stored document. Strict on structure (unknown keys, wrong
 * container types are errors), tolerant on entry shapes (malformed list
 * entries are preserved verbatim + reported by `validate()`).
 */
export function parseWorkflowDocumentText(text: string): WorkflowDocumentParseResult {
  const parsed = parseYamlDocument(text);
  if (parsed.issues.length > 0) {
    const issues = parsed.issues.map((issue) => ({
      ...issue,
      message: `The YAML could not be parsed: ${issue.message}`,
    }));
    return {
      document: null,
      errors: issues.map((issue) => issue.message),
      issues,
      discardedLegacyWorkflowState: false,
    };
  }

  if (!isYamlMapping(parsed.value)) {
    const errors = ["The workflow document must be a YAML mapping at the top level."];
    return {
      document: null,
      errors,
      issues: issuesForMessages(parsed, errors),
      discardedLegacyWorkflowState: false,
    };
  }

  const errors: string[] = [];
  const raw = parsed.value;
  const discardedLegacyWorkflowState = WORKFLOW_RUNTIME_KEY in raw;

  for (const key of Object.keys(raw)) {
    if (key === WORKFLOW_RUNTIME_KEY) continue;
    if (!WORKFLOW_DOCUMENT_KEYS.includes(key)) errors.push(`Unknown top-level key "${key}".`);
  }

  const document = new WorkflowDocument();
  document.uid = yamlString(raw.uid);
  document.type = yamlString(raw.type).toLowerCase();
  document.version = yamlString(raw.version);

  if (document.type.length > 0 && normalizeWorkflowType(document.type) === null) {
    errors.push(
      `Unknown workflow type "${yamlString(raw.type)}" (expected ${WORKFLOW_TYPES.join(", ")}).`,
    );
  }

  if ("project" in raw) {
    if (!isYamlMapping(raw.project)) {
      errors.push("'project' must be a mapping with id/name.");
    } else {
      rejectUnknownKeys(raw.project, WORKFLOW_PROJECT_KEYS, "project", errors);
      document.projectId = yamlString(raw.project.id);
      document.projectName = yamlString(raw.project.name);
    }
  }

  if ("workflow" in raw) {
    if (!isYamlMapping(raw.workflow)) {
      errors.push("'workflow' must be a mapping.");
    } else {
      for (const key of Object.keys(raw.workflow)) {
        if (!WORKFLOW_HEADER_KEYS.includes(key)) errors.push(`workflow has unknown key "${key}".`);
      }

      document.workflowName = yamlString(raw.workflow.name);
      document.description = presentString(raw.workflow, "description");
      document.expectedDuration = presentString(raw.workflow, "takes_about");
      document.language = presentString(raw.workflow, "language");
      document.product = presentString(raw.workflow, "product");
      readUserInputAllowed(raw.workflow, "workflow", errors, document);
      document.autostartDesktop = presentBoolean(raw.workflow, "autostart_desktop", "workflow", errors);
      document.autostartHeadset = presentBoolean(raw.workflow, "autostart_headset", "workflow", errors);
      document.rememberProgress = presentBoolean(raw.workflow, "remember_progress", "workflow", errors);
      document.requiresConsolePlacement = presentBoolean(
        raw.workflow,
        "requires_console_placement",
        "workflow",
        errors,
      );
      document.lockUi = presentBoolean(raw.workflow, "lock_ui", "workflow", errors);
      document.useCustomUi = presentBoolean(raw.workflow, "use_custom_ui", "workflow", errors);
      document.disableMobileKeyboard = presentBoolean(
        raw.workflow,
        "disable_mobile_keyboard",
        "workflow",
        errors,
      );
      document.knowledgeDocs = parseKnowledgeDocs(raw.workflow.knowledge, errors);
    }
  }

  if ("groups" in raw && raw.groups !== null) {
    if (!Array.isArray(raw.groups)) {
      errors.push("'groups' must be a list.");
    } else {
      raw.groups.forEach((entry, index) => {
        const group = new WorkflowGroup();
        if (typeof entry === "string") {
          group.name = entry.trim();
        } else if (isYamlMapping(entry)) {
          for (const key of Object.keys(entry)) {
            if (key !== "name") errors.push(`groups[${index}] has unknown key "${key}".`);
          }
          group.name = yamlString(entry.name);
        } else {
          errors.push(`groups[${index}] must be a name or a {name} mapping.`);
          return;
        }

        document.groups.push(group);
      });
    }
  }

  if ("steps" in raw && raw.steps !== null) {
    if (!Array.isArray(raw.steps)) {
      errors.push("'steps' must be a list.");
    } else {
      raw.steps.forEach((entry, index) => {
        const step = parseStoredStep(entry, index, errors);
        if (step !== null) document.steps.push(step);
      });
    }
  }

  if ("workflow_ui" in raw && raw.workflow_ui !== null) {
    document.workflowUi = parseWorkflowUi(raw.workflow_ui, errors);
  }

  if (errors.length > 0) {
    return {
      document: null,
      errors,
      issues: issuesForMessages(parsed, errors),
      discardedLegacyWorkflowState,
    };
  }

  rebuildGroupMembership(document);
  return { document, errors, issues: [], discardedLegacyWorkflowState };
}


/**
 * `workflow_ui` — machine-written presentation state.
 *
 * ⚠ Two severities, and the split is deliberate rather than inconsistent:
 *
 * * **Shape** violations (not a mapping, `panels_layout` not a list, an entry
 *   that is not a mapping or has no `panel`) are ERRORS. Nothing writes this
 *   section by hand, so a broken shape means the file was mangled, and the raw
 *   editor should point at it.
 * * **Value** violations (an unrecognised dock side or screen alignment, a
 *   non-finite offset) are TOLERATED, preserved verbatim, and reported by
 *   `validate()` as warnings. This is a deliberate departure from the format's
 *   "machine values are strict enums" rule, and the reason is that the rule
 *   was written for values an AUTHOR types. This section is written by the app:
 *   the only realistic source of an unknown side is a NEWER client, and
 *   refusing to open an entire workflow because a cosmetic panel offset came
 *   from a future build is exactly the "new parse error is a new way to brick a
 *   workflow" failure. A layout must never cost the author their file.
 */
function parseWorkflowUi(raw: unknown, errors: string[]): WorkflowUiState | null {
  if (!isYamlMapping(raw)) {
    errors.push("'workflow_ui' must be a mapping.");
    return null;
  }
  rejectUnknownKeys(raw, WORKFLOW_UI_KEYS, "workflow_ui", errors);

  const ui = new WorkflowUiState();
  const list = raw.panels_layout;
  if (list === undefined || list === null) return ui;
  if (!Array.isArray(list)) {
    errors.push("workflow_ui.panels_layout must be a list.");
    return ui;
  }

  list.forEach((entry, index) => {
    const where = `workflow_ui.panels_layout[${index}]`;
    if (!isYamlMapping(entry)) {
      errors.push(`${where} must be a mapping.`);
      return;
    }
    rejectUnknownKeys(entry, WORKFLOW_PANEL_PLACEMENT_KEYS, where, errors);

    const placement = new WorkflowPanelPlacement();
    placement.panel = yamlString(entry.panel).trim();
    if (placement.panel.length === 0) {
      errors.push(`${where} has no 'panel' id.`);
      return;
    }

    if (entry.dock !== undefined && entry.dock !== null) {
      if (!isYamlMapping(entry.dock)) {
        errors.push(`${where}.dock must be a mapping.`);
      } else {
        rejectUnknownKeys(entry.dock, WORKFLOW_PANEL_DOCK_KEYS, `${where}.dock`, errors);
        const dock = new WorkflowPanelDock();
        dock.parent = yamlString(entry.dock.parent).trim();
        const side = yamlString(entry.dock.side).trim().toLowerCase();
        if ((WORKFLOW_PANEL_DOCK_SIDES as readonly string[]).includes(side)) {
          dock.side = side as WorkflowPanelDockSide;
        } else if (side.length > 0) {
          dock.invalidSide = yamlString(entry.dock.side).trim();
        }
        // Absent is a legitimate state, not a fault: it means "not stated", and
        // the restore resolves it to the alignment a dock had before the key
        // existed. Only a value that is present and unreadable is preserved
        // verbatim and warned about.
        const align = yamlString(entry.dock.align).trim().toLowerCase();
        if ((WORKFLOW_PANEL_DOCK_ALIGNS as readonly string[]).includes(align)) {
          dock.align = align as WorkflowPanelDockAlign;
        } else if (align.length > 0) {
          dock.invalidAlign = yamlString(entry.dock.align).trim();
        }
        placement.dock = dock;
      }
    }

    if (entry.screen !== undefined && entry.screen !== null) {
      if (!isYamlMapping(entry.screen)) {
        errors.push(`${where}.screen must be a mapping.`);
      } else {
        rejectUnknownKeys(entry.screen, WORKFLOW_PANEL_SCREEN_KEYS, `${where}.screen`, errors);
        const screen = new WorkflowPanelScreenDock();
        const readAlign = (v: unknown): [WorkflowPanelScreenAlign | null, string] => {
          const t = yamlString(v).trim();
          if (t.length === 0) return [null, ""];
          const low = t.toLowerCase();
          return (WORKFLOW_PANEL_SCREEN_ALIGNS as readonly string[]).includes(low)
            ? [low as WorkflowPanelScreenAlign, ""]
            : [null, t];
        };
        [screen.x, screen.invalidX] = readAlign(entry.screen.x);
        [screen.y, screen.invalidY] = readAlign(entry.screen.y);
        placement.screen = screen;
      }
    }

    // A retired `free` is READ AND DROPPED. It held a viewport fraction, which
    // `position` replaced because it could not survive a change of screen — so
    // there is nothing here to carry forward, and the panel restores on its
    // designed spot until the next drag re-saves it as a position.
    if (entry.position !== undefined && entry.position !== null) {
      if (!isYamlMapping(entry.position)) {
        errors.push(`${where}.position must be a mapping.`);
      } else {
        rejectUnknownKeys(
          entry.position,
          WORKFLOW_PANEL_POSITION_KEYS,
          `${where}.position`,
          errors,
        );
        const x = Number(entry.position.x);
        const y = Number(entry.position.y);
        // A non-finite coordinate is DROPPED rather than stored — it cannot be
        // restored to anything, and `validate()` reports it.
        placement.position =
          Number.isFinite(x) && Number.isFinite(y) ? { x, y } : null;
      }
    }

    if (entry.embed !== undefined && entry.embed !== null) {
      const zone = yamlString(entry.embed).trim();
      if (zone.length > 0) placement.embed = zone;
    }

    ui.panelsLayout.push(placement);
  });

  return ui;
}

/**
 * Maps a step's optional `attachment`. Absent stays absent — a step points at
 * nothing by default, and an empty mapping is not the same as "attached to the
 * scene origin".
 */
function parseAttachment(
  raw: unknown,
  index: number,
  errors: string[],
): WorkflowStepAttachment | null {
  if (raw === null || raw === undefined) return null;
  if (!isYamlMapping(raw)) {
    errors.push(`steps[${index}].attachment must be a mapping with 'part' and/or 'point'.`);
    return null;
  }

  for (const key of Object.keys(raw)) {
    if (key !== "part" && key !== "point") {
      errors.push(`steps[${index}].attachment has unknown key "${key}".`);
    }
  }

  const attachment = new WorkflowStepAttachment();
  attachment.part = yamlString(raw.part);
  attachment.point = parseAttachmentPoint(raw.point, index, errors);
  return attachment.isEmpty ? null : attachment;
}

function parseAttachmentPoint(
  raw: unknown,
  index: number,
  errors: string[],
): { x: number; y: number; z: number } | null {
  return parseVec3Mapping(
    raw,
    `steps[${index}].attachment.point`,
    WORKFLOW_ATTACHMENT_POINT_KEYS,
    errors,
  );
}

/**
 * One `{ x, y, z }` mapping — the format's only vector shape, shared by an
 * attachment point and both halves of a `step_view`. Absent stays absent; a
 * non-numeric or non-finite axis refuses the whole vector rather than defaulting
 * it to 0, because a silently zeroed axis puts a point (or a camera) somewhere
 * nobody asked for.
 */
function parseVec3Mapping(
  raw: unknown,
  where: string,
  allowedKeys: readonly string[],
  errors: string[],
): { x: number; y: number; z: number } | null {
  if (raw === null || raw === undefined) return null;
  if (!isYamlMapping(raw)) {
    errors.push(`${where} must be a mapping with x/y/z numbers.`);
    return null;
  }

  const axes = ["x", "y", "z"] as const;
  rejectUnknownKeys(raw, allowedKeys, where, errors);
  const vector = { x: 0, y: 0, z: 0 };
  for (const axis of axes) {
    const value = raw[axis];
    if (typeof value !== "number" || !Number.isFinite(value)) {
      errors.push(`${where}.${axis} must be a number.`);
      return null;
    }
    vector[axis] = value;
  }
  return vector;
}

/**
 * Maps a step's optional `step_view`. Absent stays absent — a step without one
 * opens on its scene's saved view (`WorkflowRunModel.applyStepView`), and an
 * empty mapping is not "fly to the origin".
 *
 * Both halves are OPTIONAL here rather than required, which is deliberate: a
 * missing one is `validate()`'s to warn about and `resolveStepView`'s to fail
 * open on, where refusing the file would take a whole workflow out of the
 * editor over a camera framing. A malformed VECTOR is still an error, because
 * `{ x: "near" }` is not a coordinate under any reading.
 */
function parseStepView(raw: unknown, index: number, errors: string[]): WorkflowStepView | null {
  if (raw === null || raw === undefined) return null;
  const where = `steps[${index}].step_view`;
  if (!isYamlMapping(raw)) {
    errors.push(`${where} must be a mapping with 'position' and/or 'pivot'.`);
    return null;
  }

  rejectUnknownKeys(raw, WORKFLOW_STEP_VIEW_KEYS, where, errors);

  const view = new WorkflowStepView();
  view.position = parseVec3Mapping(
    raw.position,
    `${where}.position`,
    WORKFLOW_STEP_VIEW_VECTOR_KEYS,
    errors,
  );
  view.pivot = parseVec3Mapping(
    raw.pivot,
    `${where}.pivot`,
    WORKFLOW_STEP_VIEW_VECTOR_KEYS,
    errors,
  );
  return view.isEmpty ? null : view;
}

/**
 * Maps a step's optional `completion_condition`. Absent stays absent — Next is
 * available immediately by default, and an empty mapping is not "gated by
 * nothing".
 *
 * `type` is a STRICT enum like every other machine value: an unrecognised word
 * refuses the file naming it, rather than quietly gating Next on something the
 * runtime does not implement. `delay_seconds` is type-checked here (the
 * `attachment.point` rule — a number is a number) but its RANGE is
 * `validate()`'s business, so a zero or a missing one lands in the panel's
 * issue queue instead of taking the whole document out of the editor.
 */
function parseCompletionCondition(
  raw: unknown,
  index: number,
  errors: string[],
): WorkflowStepCompletionCondition | null {
  if (raw === null || raw === undefined) return null;
  const where = `steps[${index}].completion_condition`;
  if (!isYamlMapping(raw)) {
    errors.push(
      `${where} must be a mapping with a 'type' (${COMPLETION_CONDITIONS.join(" or ")}).`,
    );
    return null;
  }

  rejectUnknownKeys(raw, WORKFLOW_COMPLETION_CONDITION_KEYS, where, errors);

  const condition = new WorkflowStepCompletionCondition();
  const typeWord = yamlString(raw.type);
  if (typeWord.length > 0) {
    const normalized = normalizeCompletionCondition(typeWord);
    if (normalized === null) {
      errors.push(
        `${where} has an unknown type "${typeWord}" (expected ${COMPLETION_CONDITIONS.join(", ")}).`,
      );
      condition.invalidType = typeWord;
    } else {
      condition.type = normalized;
    }
  }

  if ("delay_seconds" in raw && raw.delay_seconds !== null && raw.delay_seconds !== undefined) {
    const value = raw.delay_seconds;
    const parsed = typeof value === "number" ? value : Number(String(value).trim());
    if (!Number.isFinite(parsed)) {
      errors.push(`${where}.delay_seconds must be a number of seconds.`);
    } else {
      condition.delaySeconds = parsed;
    }
  }

  return condition.isEmpty ? null : condition;
}

/**
 * Maps a step's optional `media`. Absent stays absent — a step shows no media
 * by default, and an empty mapping is not "media with no source".
 *
 * `type` is a STRICT enum like every other machine value: an unrecognised word
 * refuses the file, naming it and the allowed values, instead of quietly
 * playing a video as an image. Timecodes stay VERBATIM text — a YAML author
 * writing `start: 90` means 90 seconds, so a number is stringified rather than
 * refused, and `validate()` is where the text becomes a number.
 */
function parseMedia(raw: unknown, index: number, errors: string[]): WorkflowStepMedia | null {
  if (raw === null || raw === undefined) return null;
  const where = `steps[${index}].media`;
  if (!isYamlMapping(raw)) {
    errors.push(`${where} must be a mapping with a 'url' and an optional 'type'.`);
    return null;
  }

  rejectUnknownKeys(raw, WORKFLOW_MEDIA_KEYS, where, errors);

  const media = new WorkflowStepMedia();
  const typeWord = yamlString(raw.type);
  if (typeWord.length > 0) {
    const normalized = normalizeMediaType(typeWord);
    if (normalized === null) {
      errors.push(`${where} has an unknown type "${typeWord}" (expected ${MEDIA_TYPES.join(", ")}).`);
      media.invalidType = typeWord;
    } else {
      media.type = normalized;
    }
  }

  const fitWord = yamlString(raw.fit);
  if (fitWord.length > 0) {
    const normalized = normalizeMediaFit(fitWord);
    if (normalized === null) {
      errors.push(`${where} has an unknown fit "${fitWord}" (expected ${MEDIA_FITS.join(", ")}).`);
      media.invalidFit = fitWord;
    } else {
      media.fit = normalized;
    }
  }

  media.url = yamlString(raw.url);
  media.name = yamlString(raw.name);
  media.description = yamlString(raw.description);
  media.transcript = typeof raw.transcript === "string" ? raw.transcript.trim() : "";
  media.startTime = yamlTimecode(raw.start);
  media.endTime = yamlTimecode(raw.end);
  // A strict boolean like every other one in the format: `loop: yes` refuses
  // the file rather than being coerced, so there is no invalid-word field to
  // round-trip the way `type` and `fit` need one.
  media.loop = presentBoolean(raw, "loop", where, errors);
  media.autoplay = presentBoolean(raw, "autoplay", where, errors);
  return media.isEmpty ? null : media;
}

/** A timecode as authored: a string kept verbatim, a bare number stringified. */
function yamlTimecode(value: unknown): string {
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  return yamlString(value);
}

/**
 * Maps `workflow.knowledge.include`. Tolerant like every other list in the
 * format: an entry the codec cannot shape into `{ doc }` is preserved verbatim
 * so a rewrite cannot delete it, and `validate()` names it by index.
 */
function parseKnowledgeDocs(raw: unknown, errors: string[]): WorkflowKnowledgeDoc[] {
  if (raw === null || raw === undefined) return [];
  if (!isYamlMapping(raw)) {
    errors.push("workflow.knowledge must be a mapping with an 'include' list.");
    return [];
  }

  for (const key of Object.keys(raw)) {
    if (key !== "include") errors.push(`workflow.knowledge has unknown key "${key}".`);
  }

  const include = raw.include;
  if (include === null || include === undefined) return [];
  if (!Array.isArray(include)) {
    errors.push("workflow.knowledge.include must be a list.");
    return [];
  }

  return include.map((entry, includeIndex) => {
    const doc = new WorkflowKnowledgeDoc();
    const where = `workflow.knowledge.include[${includeIndex}]`;
    if (typeof entry === "string") {
      doc.doc = entry.trim();
      return doc;
    }
    if (isYamlMapping(entry)) {
      rejectUnknownKeys(entry, ["doc"], where, errors);
      if (typeof entry.doc === "string") {
        doc.doc = entry.doc.trim();
        return doc;
      }
    }
    doc.invalidRaw = entry;
    return doc;
  });
}

/**
 * Reads a `boolean | null` field with the presence convention: absent -> null
 * (inherit / default), present -> the value. A non-boolean is an error rather
 * than a coercion — `autostart_desktop: "no"` coerces to TRUE in JS, which is
 * the opposite of what it says.
 */
function presentBoolean(
  mapping: Record<string, unknown>,
  key: string,
  where: string,
  errors: string[],
): boolean | null {
  if (!(key in mapping) || mapping[key] === null || mapping[key] === undefined) return null;
  const value = mapping[key];
  if (typeof value !== "boolean") {
    errors.push(`${where}.${key} must be true or false.`);
    return null;
  }
  return value;
}

/**
 * Reads `user_input_allowed` onto a header or step.
 *
 * Absent -> null (inherit / nothing). Present -> a LIST, and it must be one: a
 * bare string is refused rather than wrapped, because `user_input_allowed:
 * ai_input` reading as `[ai_input]` would give the format two spellings forever.
 *
 * Members are stored VERBATIM, `all` included — the wildcard is resolved when
 * the run reads it, never unfolded here (see `USER_INPUT_ALL`).
 *
 * A member this build does not know is NOT an error — it is kept verbatim in
 * `invalidUserInputAllowed`, re-serialized in place, and reported by `validate`.
 * It opens nothing, so the run stays exactly as guided as the rest of the list
 * says; refusing the file would take a whole workflow offline over a typo.
 */
function readUserInputAllowed(
  mapping: Record<string, unknown>,
  where: string,
  errors: string[],
  target: { userInputAllowed: WorkflowUserInputAllowance[] | null; invalidUserInputAllowed: string[] },
): void {
  const key = "user_input_allowed";
  if (!(key in mapping) || mapping[key] === null || mapping[key] === undefined) {
    target.userInputAllowed = null;
    target.invalidUserInputAllowed = [];
    return;
  }
  const value = mapping[key];
  if (!Array.isArray(value)) {
    errors.push(`${where}.${key} must be a list (${USER_INPUT_ALLOWANCES.join(", ")}).`);
    target.userInputAllowed = null;
    target.invalidUserInputAllowed = [];
    return;
  }
  const split = splitUserInputAllowances(value);
  target.userInputAllowed = split.known;
  target.invalidUserInputAllowed = split.unknown;
}

/** The patch leg: a list, `null` to clear, and anything else is an error. */
function readPatchUserInputAllowed(
  mapping: Record<string, unknown>,
  where: string,
  errors: string[],
): string[] | null {
  const value = mapping.user_input_allowed;
  if (value === null || value === undefined) return null;
  if (!Array.isArray(value)) {
    errors.push(
      `${where}.user_input_allowed must be a list (${USER_INPUT_ALLOWANCES.join(", ")}) or null.`,
    );
    return null;
  }
  return value.map((entry) => yamlString(entry).trim()).filter((entry) => entry.length > 0);
}

/**
 * Writes a patched list onto a header or step. `null` clears it back to
 * inherit / nothing; an empty list is kept as an explicit "nothing here", which
 * is how a mostly-open workflow closes one step down.
 */
function applyPatchUserInputAllowed(
  target: { userInputAllowed: WorkflowUserInputAllowance[] | null; invalidUserInputAllowed: string[] },
  value: string[] | null,
): void {
  if (value === null) {
    target.userInputAllowed = null;
    target.invalidUserInputAllowed = [];
    return;
  }
  const split = splitUserInputAllowances(value);
  target.userInputAllowed = split.known;
  target.invalidUserInputAllowed = split.unknown;
}

/** Dedupes and partitions raw members into recognised and not. */
function splitUserInputAllowances(value: readonly unknown[]): {
  known: WorkflowUserInputAllowance[];
  unknown: string[];
} {
  const known: WorkflowUserInputAllowance[] = [];
  const unknown: string[] = [];
  for (const entry of value) {
    const text = yamlString(entry).trim();
    if (text.length === 0) continue;
    const allowance = normalizeUserInputAllowance(text);
    if (allowance === null) {
      if (!unknown.includes(text)) unknown.push(text);
    } else if (!known.includes(allowance)) {
      known.push(allowance);
    }
  }
  return { known, unknown };
}

/** Reads a `string | null` field with the presence convention: absent -> null. */
function presentString(mapping: Record<string, unknown>, key: string): string | null {
  if (!(key in mapping) || mapping[key] === null || mapping[key] === undefined) return null;
  const value = mapping[key];
  return typeof value === "string" ? value : String(value);
}

function parseStoredStep(entry: unknown, index: number, errors: string[]): WorkflowStep | null {
  if (!isYamlMapping(entry)) {
    errors.push(`steps[${index}] must be a mapping.`);
    return null;
  }

  for (const key of Object.keys(entry)) {
    if (!WORKFLOW_STEP_KEYS.includes(key)) errors.push(`steps[${index}] has unknown key "${key}".`);
  }

  const step = new WorkflowStep();
  step.name = yamlString(entry.name);
  if (step.name.length === 0) errors.push(`steps[${index}] needs a 'name'.`);
  step.stepId = yamlString(entry.id);
  step.disabled = presentBoolean(entry, "disabled", `steps[${index}]`, errors);
  step.groupName = yamlString(entry.group);
  step.scene = yamlSceneRef(entry.scene);
  step.expectedDuration = presentString(entry, "takes_about");
  step.stepContext = presentString(entry, "step_context");
  step.stepInstructions = presentString(entry, "step_instructions");
  step.backButton = presentString(entry, "back_button");
  step.nextButton = presentString(entry, "next_button");
  step.nextTarget = normalizeNextTarget(presentString(entry, "next_target"));
  step.isCompletionStep = presentBoolean(entry, "is_completion_step", `steps[${index}]`, errors);
  step.completionCondition = parseCompletionCondition(entry.completion_condition, index, errors);
  readUserInputAllowed(entry, `steps[${index}]`, errors, step);
  step.attachment = parseAttachment(entry.attachment, index, errors);
  step.stepView = parseStepView(entry.step_view, index, errors);
  step.media = parseMedia(entry.media, index, errors);

  if ("triggers" in entry && entry.triggers !== null) {
    if (!Array.isArray(entry.triggers)) {
      errors.push(`steps[${index}].triggers must be a list.`);
    } else {
      entry.triggers.forEach((rawTrigger, triggerIndex) => {
        step.triggers.push(
          storedTriggerFromYaml(rawTrigger, `steps[${index}].triggers[${triggerIndex}]`, errors),
        );
      });
    }
  }

  if ("scripts" in entry && entry.scripts !== null) {
    if (!Array.isArray(entry.scripts)) {
      errors.push(`steps[${index}].scripts must be a list.`);
    } else {
      entry.scripts.forEach((rawScript, scriptIndex) => {
        step.scripts.push(
          storedScriptFromYaml(rawScript, `steps[${index}].scripts[${scriptIndex}]`, errors),
        );
      });
    }
  }

  return step;
}

/**
 * Maps one stored trigger entry. Unknown keys and enum values refuse the
 * parse. A malformed non-mapping (or an authored `{ raw: "<line>" }`) is
 * preserved verbatim for `validate()` to name.
 */
function storedTriggerFromYaml(
  raw: unknown,
  where: string,
  errors: string[],
): WorkflowStepTrigger {
  const trigger = new WorkflowStepTrigger();
  if (!isYamlMapping(raw)) {
    trigger.invalidRaw = raw;
    return trigger;
  }

  rejectUnknownKeys(raw, WORKFLOW_TRIGGER_KEYS, where, errors);

  // An unparseable authored line round-trips as { raw: "<line>" }.
  const rawLine = yamlString(raw.raw);
  if (rawLine.length > 0) {
    trigger.invalidText = rawLine;
    return trigger;
  }

  const rawType = yamlString(raw.type);
  const type = rawType.length === 0 ? "button" : normalizeStepTriggerType(rawType);
  if (type === null) {
    errors.push(
      `${where} has unknown type "${rawType}" (expected ${STEP_TRIGGER_TYPES.join(", ")}).`,
    );
    trigger.invalidType = rawType;
  } else {
    trigger.type = type;
  }

  trigger.label = yamlString(raw.label);
  if (trigger.label.length === 0) {
    errors.push(`${where} needs a 'label' (the button text the learner sees).`);
  }

  trigger.goTo = yamlString(raw.go_to);
  if (trigger.goTo.length === 0) {
    errors.push(`${where} needs a 'go_to' target (a step or group name).`);
  }

  return trigger;
}

/** Maps one stored `scripts:` entry; malformed entries are preserved verbatim. */
function storedScriptFromYaml(
  raw: unknown,
  where: string,
  errors: string[],
): WorkflowScript {
  const script = new WorkflowScript();
  if (!isYamlMapping(raw)) {
    script.invalidRaw = raw;
    return script;
  }

  rejectUnknownKeys(raw, WORKFLOW_SCRIPT_KEYS, where, errors);

  script.name = yamlString(raw.name);
  const id = yamlString(raw.id);
  if (id.length === 0) {
    errors.push(`${where} needs an 'id'.`);
  } else {
    script.id = id;
  }

  const rawTrigger = yamlString(raw.trigger);
  if (rawTrigger.length === 0) {
    script.trigger = "manual";
  } else {
    const trigger = normalizeScriptTrigger(rawTrigger);
    if (trigger === null) {
      errors.push(
        `${where} has an unknown trigger "${rawTrigger}" (expected ${SCRIPT_TRIGGERS.join(", ")}).`,
      );
      script.invalidTrigger = rawTrigger;
    } else {
      script.trigger = trigger;
    }
  }

  if ("revert" in raw) {
    const rawRevert = yamlString(raw.revert);
    const revert = normalizeScriptRevert(rawRevert);
    if (revert === null) {
      errors.push(
        `${where} has an unknown revert "${yamlString(raw.revert)}" (expected ${SCRIPT_REVERTS.join(", ")}).`,
      );
      script.invalidRevert = rawRevert;
    } else {
      script.revert = revert;
    }
  }

  if (Array.isArray(raw.steps)) {
    raw.steps.forEach((rawStep, stepIndex) => {
      script.steps.push(storedStepFromYaml(rawStep, `${where}.steps[${stepIndex}]`, errors));
    });
  }

  return script;
}

/**
 * Maps one step. Two shapes: `{ say: "..." }` for a verbatim spoken line, or
 * `{ tool: "isolate", args: { ... } }` for a tool call. A bare string is
 * accepted as a say step. Anything else is preserved verbatim + reported.
 */
function storedStepFromYaml(
  raw: unknown,
  where: string,
  errors: string[],
): WorkflowScriptStep {
  const step = new WorkflowScriptStep();
  if (typeof raw === "string") {
    const text = raw.trim();
    if (text.length > 0) {
      step.sayText = text;
      return step;
    }

    step.invalidRaw = raw;
    return step;
  }

  if (!isYamlMapping(raw)) {
    step.invalidRaw = raw;
    return step;
  }

  const parsed = parseScriptStepMapping(raw, where, errors);
  if (parsed === null) {
    step.invalidRaw = raw;
    return step;
  }

  if (parsed.say !== undefined) {
    step.sayText = parsed.say;
    return step;
  }

  step.tool = parsed.tool ?? "";
  step.args = parsed.args ?? null;
  return step;
}

/**
 * Enforces the script-step schema: `say` XOR `tool` (+ optional `args` only
 * with `tool`). Unknown `args` keys are refused when the tool is in the web
 * registry; unknown tools skip that check (`validate()` warns instead).
 *
 * An unknown KEY is the ONLY thing about an argument refused here, and that is
 * deliberate. A parse error makes the whole document unreadable — the panel
 * drops to the raw editor and the workflow stops playing — so one bad line can
 * take a project's training offline. An unknown key earns that: it names no
 * parameter and nothing normalises it into one. An argument's TYPE, its enum
 * membership and whether a required one is present are reported by `validate()`
 * as warnings instead (`validateScriptCommandUsage`), because the executors are
 * more tolerant than the declared schema — they fold enum values and accept
 * documented aliases, so `semanticColor: Hazard` and `red` both play, and
 * refusing them would refuse a file that works.
 */
function parseScriptStepMapping(
  raw: Record<string, unknown>,
  where: string,
  errors: string[],
): WorkflowYamlScriptStep | null {
  const hasSay = "say" in raw;
  const hasTool = "tool" in raw;

  if (hasSay && hasTool) {
    errors.push(`${where} cannot mix 'say' and 'tool'; use one or the other.`);
    return null;
  }

  if (hasSay) {
    rejectUnknownKeys(raw, ["say"], where, errors);
    const say = yamlString(raw.say);
    if (say.length === 0) {
      errors.push(`${where} needs a non-empty 'say'.`);
      return null;
    }
    return { say };
  }

  if (hasTool) {
    rejectUnknownKeys(raw, ["tool", "args"], where, errors);
    const tool = yamlString(raw.tool);
    if (tool.length === 0) {
      errors.push(`${where} needs a non-empty 'tool'.`);
      return null;
    }

    if ("args" in raw && raw.args !== null && raw.args !== undefined && !isYamlMapping(raw.args)) {
      errors.push(`${where}.args must be a mapping.`);
      return null;
    }

    const args = isYamlMapping(raw.args) ? raw.args : undefined;
    if (args) rejectUnknownToolArgs(tool, args, where, errors);
    return args ? { tool, args } : { tool };
  }

  rejectUnknownKeys(raw, WORKFLOW_SCRIPT_STEP_KEYS, where, errors);
  errors.push(`${where} needs either 'say' or 'tool'.`);
  return null;
}

// ---------------------------------------------------------------------------
// Patch shape (the sparse edit document)
// ---------------------------------------------------------------------------

/**
 * One executable step INSIDE a prepared script — distinct from
 * {@link WorkflowYamlStep}, which is a step of the workflow itself. The two
 * senses of "step" are unavoidable now that a workflow's units are steps:
 * script steps are always reached through a `scripts[].steps[]` path.
 */
export interface WorkflowYamlScriptStep {
  say?: string;
  tool?: string;
  args?: Record<string, unknown>;
}

export interface WorkflowYamlScript {
  id: string;
  name?: string;
  trigger: string;
  revert?: string;
  steps: WorkflowYamlScriptStep[];
}

export interface WorkflowYamlTrigger {
  type: string;
  label: string;
  go_to: string;
}

export interface WorkflowYamlStep {
  /** Stable machine id; preferred over `name` so an API rename is not a delete+create. */
  id?: string;
  name: string;
  /**
   * Parks the step: it stays in the file and stays editable, but no run reaches
   * it. `null` clears it back to the default (absent = false, i.e. live).
   */
  disabled?: boolean | null;
  /**
   * Group name. A string moves the step into that group (creating the group
   * when new); an explicit `null` moves it out (to the end of the ungrouped
   * head run).
   */
  group?: string | null;
  scene?: string | null;
  takes_about?: string | null;
  /** The step's INTERNAL one-line summary for the guide; never shown to the learner. */
  step_context?: string | null;
  /** The learner-facing copy the play panel shows (empty or absent = it shows none). */
  step_instructions?: string | null;
  back_button?: string | null;
  next_button?: string | null;
  /** Where this step's Next goes (a step or group name); `null` or empty = the next step in the file. */
  next_target?: string | null;
  /**
   * Pressing Next on this step ENDS the run instead of advancing; `null` clears
   * it back to the default (absent = false).
   */
  is_completion_step?: boolean | null;
  /**
   * What has to happen before Next becomes available. Presence REPLACES the
   * whole block — it is one thing, like `attachment` and `media`, so a patch
   * naming only `delay_seconds` must not inherit a `type` it did not choose —
   * and `null` clears it back to "Next is available immediately".
   */
  completion_condition?: WorkflowYamlCompletionCondition | null;
  /** Per-step override of the workflow's setting; `null` inherits. */
  user_input_allowed?: string[] | null;
  /** Scene attachment; `null` clears it. */
  attachment?: { part?: string; point?: { x: number; y: number; z: number } | null } | null;
  /**
   * Where the camera flies when the step is activated. Presence REPLACES the
   * whole block (a pose is one thing, like `attachment` and `media`, so a patch
   * naming only `position` must not inherit a `pivot` from the framing it is
   * replacing — the camera would end up looking at the wrong point from the
   * right place); `null` clears it back to "this step does not move the
   * camera".
   */
  step_view?: WorkflowYamlStepView | null;
  /**
   * Image or video shown beside the play panel. Presence REPLACES the whole
   * media block (it is one thing, not a bag of independent fields, so a patch
   * that named only `url` and inherited the previous `transcript` would leave
   * a transcript describing a different video); `null` clears it.
   */
  media?: WorkflowYamlMedia | null;
  /** Panel trigger buttons; like `scripts`, presence REPLACES the whole list. */
  triggers?: WorkflowYamlTrigger[] | null;
  scripts?: WorkflowYamlScript[];
}

export interface WorkflowYamlCompletionCondition {
  type?: WorkflowCompletionConditionType;
  delay_seconds?: number;
}

export interface WorkflowYamlStepView {
  position?: { x: number; y: number; z: number } | null;
  pivot?: { x: number; y: number; z: number } | null;
}

export interface WorkflowYamlMedia {
  type?: WorkflowMediaType;
  fit?: WorkflowMediaFit;
  url?: string;
  name?: string;
  description?: string;
  transcript?: string;
  start?: string;
  end?: string;
  loop?: boolean;
  autoplay?: boolean;
}

export interface WorkflowYamlPatch {
  workflow?: {
    name?: string;
    description?: string | null;
    takes_about?: string | null;
    language?: string | null;
    product?: string | null;
    user_input_allowed?: string[] | null;
    autostart_desktop?: boolean | null;
    autostart_headset?: boolean | null;
    remember_progress?: boolean | null;
    requires_console_placement?: boolean | null;
    lock_ui?: boolean | null;
    use_custom_ui?: boolean | null;
    disable_mobile_keyboard?: boolean | null;
  };
  steps?: WorkflowYamlStep[];
}

export interface WorkflowYamlParseResult {
  readonly doc: WorkflowYamlPatch | null;
  readonly errors: string[];
  readonly issues: WorkflowYamlIssue[];
}

/**
 * The two top-level keys a PATCH may carry. Identity and retired shared state are named
 * separately below so a caller sending one gets the reason it is refused rather
 * than a bare "unknown key".
 */
export const WORKFLOW_PATCH_KEYS = ["workflow", "steps"];

/** File-model-owned identity keys — never patchable. */
export const WORKFLOW_IDENTITY_KEYS = ["uid", "type", "version", "project"];

/** Retired shared-state key — accepted/discarded only by full-file parsing. */
export const WORKFLOW_RUNTIME_KEY = "workflow_state";

/**
 * Header keys a patch may set. Narrower than {@link WORKFLOW_HEADER_KEYS}:
 * `knowledge` is machine-owned and has no patch leg. Keep in step with the
 * `if ("…" in raw.workflow)` branches below — this list is what refuses
 * everything they do not handle.
 */
export const WORKFLOW_PATCH_HEADER_KEYS = [
  "name",
  "description",
  "takes_about",
  "language",
  "product",
  "user_input_allowed",
  "autostart_desktop",
  "autostart_headset",
  "remember_progress",
  "requires_console_placement",
  "lock_ui",
  "use_custom_ui",
  "disable_mobile_keyboard",
];

export const WORKFLOW_PATCH_STEP_KEYS = [
  "id",
  "name",
  "disabled",
  "group",
  "scene",
  "takes_about",
  "step_context",
  "step_instructions",
  "back_button",
  "next_button",
  "next_target",
  "is_completion_step",
  "completion_condition",
  "user_input_allowed",
  "attachment",
  "step_view",
  "media",
  "triggers",
  "scripts",
];

/**
 * Parses and shape-validates an incoming YAML patch. Unknown keys are
 * reported rather than ignored — silently dropping a misspelled key is the
 * exact failure mode the typed format exists to avoid. Identity keys and
 * `workflow_state` are refused by name.
 */
export function parseWorkflowPatchText(text: string): WorkflowYamlParseResult {
  const parsed = parseYamlDocument(text);
  if (parsed.issues.length > 0) {
    const issues = parsed.issues.map((issue) => ({
      ...issue,
      message: `The YAML could not be parsed: ${issue.message}`,
    }));
    return {
      doc: null,
      errors: issues.map((issue) => issue.message),
      issues,
    };
  }

  if (!isYamlMapping(parsed.value)) {
    const errors = ["The YAML document must be a mapping at the top level."];
    return {
      doc: null,
      errors,
      issues: issuesForMessages(parsed, errors),
    };
  }

  const errors: string[] = [];
  const raw = parsed.value;

  if (WORKFLOW_RUNTIME_KEY in raw) {
    errors.push(
      "'workflow_state' is retired and cannot be written here; per-user progress is stored in DynamoDB.",
    );
  }

  for (const identityKey of WORKFLOW_IDENTITY_KEYS) {
    if (identityKey in raw) {
      errors.push(`'${identityKey}' is identity metadata the file model owns; it cannot be patched.`);
    }
  }

  if (WORKFLOW_UI_KEY in raw) {
    errors.push(
      "'workflow_ui' is machine-owned presentation state and has no patch path; it cannot be written here.",
    );
  }

  /*
    `workflow_ui` is listed here as well as refused by name above. Both halves
    are needed: without the named refusal the caller gets a bare "unknown key"
    that does not say WHY, and without membership here it gets that bare error
    IN ADDITION to the real one. The `knowledge` refusal one level down is the
    pattern.

    Deliberately NOT added to WORKFLOW_IDENTITY_KEYS, which would have yielded a
    refusal for free: that array is also rendered as the published schema's
    IDENTITY row, which would tell the agent that panel layout is file metadata
    the file model stamps.
  */
  const knownTopLevel = [
    ...WORKFLOW_PATCH_KEYS,
    WORKFLOW_RUNTIME_KEY,
    WORKFLOW_UI_KEY,
    ...WORKFLOW_IDENTITY_KEYS,
  ];
  for (const key of Object.keys(raw)) {
    if (!knownTopLevel.includes(key)) {
      errors.push(`Unknown top-level key "${key}".`);
    }
  }

  const doc: WorkflowYamlPatch = {};

  if ("workflow" in raw) {
    if (!isYamlMapping(raw.workflow)) {
      errors.push("'workflow' must be a mapping.");
    } else {
      // Same rule the stored parse and the step patch already follow: a key
      // this path does not handle is REPORTED, never silently dropped. Without
      // it a misspelled header key returned a clean "updated" report having
      // changed nothing — the exact failure the typed format exists to avoid.
      for (const key of Object.keys(raw.workflow)) {
        if (key === "knowledge") {
          errors.push(
            "'workflow.knowledge' is machine-owned and has no patch path; it cannot be written here.",
          );
        } else if (!WORKFLOW_PATCH_HEADER_KEYS.includes(key)) {
          errors.push(`workflow has unknown key "${key}".`);
        }
      }

      doc.workflow = {};
      if ("name" in raw.workflow) doc.workflow.name = yamlString(raw.workflow.name);
      if ("description" in raw.workflow) {
        doc.workflow.description = nullableString(raw.workflow.description);
      }
      if ("takes_about" in raw.workflow) {
        doc.workflow.takes_about = nullableString(raw.workflow.takes_about);
      }
      if ("language" in raw.workflow) {
        doc.workflow.language = nullableString(raw.workflow.language);
      }
      if ("product" in raw.workflow) {
        doc.workflow.product = nullableString(raw.workflow.product);
      }
      if ("user_input_allowed" in raw.workflow) {
        doc.workflow.user_input_allowed = readPatchUserInputAllowed(
          raw.workflow,
          "workflow",
          errors,
        );
      }
      if ("autostart_desktop" in raw.workflow) {
        doc.workflow.autostart_desktop = presentBoolean(
          raw.workflow,
          "autostart_desktop",
          "workflow",
          errors,
        );
      }
      if ("autostart_headset" in raw.workflow) {
        doc.workflow.autostart_headset = presentBoolean(
          raw.workflow,
          "autostart_headset",
          "workflow",
          errors,
        );
      }
      if ("remember_progress" in raw.workflow) {
        doc.workflow.remember_progress = presentBoolean(
          raw.workflow,
          "remember_progress",
          "workflow",
          errors,
        );
      }
      if ("requires_console_placement" in raw.workflow) {
        doc.workflow.requires_console_placement = presentBoolean(
          raw.workflow,
          "requires_console_placement",
          "workflow",
          errors,
        );
      }
      if ("lock_ui" in raw.workflow) {
        doc.workflow.lock_ui = presentBoolean(raw.workflow, "lock_ui", "workflow", errors);
      }
      if ("disable_mobile_keyboard" in raw.workflow) {
        doc.workflow.disable_mobile_keyboard = presentBoolean(
          raw.workflow,
          "disable_mobile_keyboard",
          "workflow",
          errors,
        );
      }
      if ("use_custom_ui" in raw.workflow) {
        doc.workflow.use_custom_ui = presentBoolean(
          raw.workflow,
          "use_custom_ui",
          "workflow",
          errors,
        );
      }
    }
  }

  if ("steps" in raw) {
    if (!Array.isArray(raw.steps)) {
      errors.push("'steps' must be a list.");
    } else {
      doc.steps = [];
      raw.steps.forEach((entry, index) => {
        const step = parsePatchStep(entry, index, errors);
        if (step !== null) doc.steps!.push(step);
      });
    }
  }

  return {
    doc: errors.length > 0 ? null : doc,
    errors,
    issues: issuesForMessages(parsed, errors),
  };
}

function parsePatchStep(
  entry: unknown,
  index: number,
  errors: string[],
): WorkflowYamlStep | null {
  if (!isYamlMapping(entry)) {
    errors.push(`steps[${index}] must be a mapping.`);
    return null;
  }

  const name = yamlString(entry.name);
  const id = yamlString(entry.id);
  if (name.length === 0 && id.length === 0) {
    errors.push(`steps[${index}] needs a 'name' (or an 'id' matching an existing step).`);
    return null;
  }

  for (const key of Object.keys(entry)) {
    if (!WORKFLOW_PATCH_STEP_KEYS.includes(key)) {
      errors.push(`steps[${index}] has unknown key "${key}".`);
    }
  }

  const step: WorkflowYamlStep = { name };
  if (id.length > 0) step.id = id;
  if ("group" in entry) {
    const group = nullableString(entry.group);
    if (group !== null && group.length === 0) {
      errors.push(
        `steps[${index}].group must be a group name, or null to move the step out of its group.`,
      );
    } else {
      step.group = group;
    }
  }

  if ("disabled" in entry) {
    step.disabled = presentBoolean(entry, "disabled", `steps[${index}]`, errors);
  }
  if ("scene" in entry) step.scene = yamlSceneRef(entry.scene);
  if ("takes_about" in entry) step.takes_about = nullableString(entry.takes_about);
  if ("step_context" in entry) step.step_context = nullableString(entry.step_context);
  if ("step_instructions" in entry) {
    step.step_instructions = nullableString(entry.step_instructions);
  }
  if ("back_button" in entry) step.back_button = nullableString(entry.back_button);
  if ("next_button" in entry) step.next_button = nullableString(entry.next_button);
  if ("next_target" in entry) step.next_target = nullableString(entry.next_target);
  if ("is_completion_step" in entry) {
    step.is_completion_step = presentBoolean(
      entry,
      "is_completion_step",
      `steps[${index}]`,
      errors,
    );
  }
  if ("completion_condition" in entry) {
    if (entry.completion_condition === null) {
      step.completion_condition = null;
    } else {
      const parsed = parseCompletionCondition(entry.completion_condition, index, errors);
      if (parsed !== null) {
        step.completion_condition = {
          ...(parsed.type !== null ? { type: parsed.type } : {}),
          ...(parsed.delaySeconds !== null ? { delay_seconds: parsed.delaySeconds } : {}),
        };
      }
    }
  }

  if ("user_input_allowed" in entry) {
    step.user_input_allowed = readPatchUserInputAllowed(entry, `steps[${index}]`, errors);
  }
  if ("attachment" in entry) {
    if (entry.attachment === null) {
      step.attachment = null;
    } else {
      const parsed = parseAttachment(entry.attachment, index, errors);
      if (parsed !== null) {
        step.attachment = {
          part: parsed.part,
          point: parsed.point,
        };
      }
    }
  }

  if ("step_view" in entry) {
    if (entry.step_view === null) {
      step.step_view = null;
    } else {
      const parsed = parseStepView(entry.step_view, index, errors);
      if (parsed !== null) {
        step.step_view = { position: parsed.position, pivot: parsed.pivot };
      }
    }
  }

  if ("media" in entry) {
    if (entry.media === null) {
      step.media = null;
    } else {
      const parsed = parseMedia(entry.media, index, errors);
      if (parsed !== null) {
        step.media = {
          ...(parsed.type !== null ? { type: parsed.type } : {}),
          ...(parsed.fit !== null ? { fit: parsed.fit } : {}),
          url: parsed.url,
          name: parsed.name,
          description: parsed.description,
          transcript: parsed.transcript,
          start: parsed.startTime,
          end: parsed.endTime,
          ...(parsed.loop !== null ? { loop: parsed.loop } : {}),
          ...(parsed.autoplay !== null ? { autoplay: parsed.autoplay } : {}),
        };
      }
    }
  }

  if ("triggers" in entry) {
    if (entry.triggers === null) {
      // Explicit null clears the list, mirroring how `scripts: []` empties it.
      step.triggers = [];
    } else if (!Array.isArray(entry.triggers)) {
      errors.push(`steps[${index}].triggers must be a list (or null to clear it).`);
    } else {
      step.triggers = [];
      entry.triggers.forEach((trigger, triggerIndex) => {
        const parsed = parsePatchTrigger(trigger, index, triggerIndex, errors);
        if (parsed !== null) step.triggers!.push(parsed);
      });
    }
  }

  if ("scripts" in entry) {
    if (!Array.isArray(entry.scripts)) {
      errors.push(`steps[${index}].scripts must be a list.`);
    } else {
      step.scripts = [];
      entry.scripts.forEach((script, scriptIndex) => {
        const parsed = parsePatchScript(script, index, scriptIndex, errors);
        if (parsed !== null) step.scripts!.push(parsed);
      });
    }
  }

  return step;
}

/**
 * Parses one `triggers:` entry, refusing anything malformed by name. Absent
 * `type` defaults to `button` (the only kind); an unrecognized type is an
 * error, never a silent fallback — same strict-enum discipline as script
 * triggers.
 */
function parsePatchTrigger(
  entry: unknown,
  stepIndex: number,
  triggerIndex: number,
  errors: string[],
): WorkflowYamlTrigger | null {
  const where = `steps[${stepIndex}].triggers[${triggerIndex}]`;
  if (!isYamlMapping(entry)) {
    errors.push(`${where} must be a mapping.`);
    return null;
  }

  for (const key of Object.keys(entry)) {
    if (!WORKFLOW_TRIGGER_KEYS.includes(key) || key === "raw") {
      errors.push(`${where} has unknown key "${key}".`);
    }
  }

  const rawType = yamlString(entry.type);
  const type = rawType.length === 0 ? "button" : normalizeStepTriggerType(rawType);
  if (type === null) {
    errors.push(
      `${where} has unknown type "${rawType}" (expected ${STEP_TRIGGER_TYPES.join(", ")}).`,
    );
    return null;
  }

  const label = yamlString(entry.label);
  if (label.length === 0) {
    errors.push(`${where} needs a 'label' (the button text the learner sees).`);
    return null;
  }

  const goTo = yamlString(entry.go_to);
  if (goTo.length === 0) {
    errors.push(`${where} ("${label}") needs a 'go_to' target (a step or group name).`);
    return null;
  }

  return { type, label, go_to: goTo };
}

function parsePatchScript(
  entry: unknown,
  stepIndex: number,
  scriptIndex: number,
  errors: string[],
): WorkflowYamlScript | null {
  const where = `steps[${stepIndex}].scripts[${scriptIndex}]`;
  if (!isYamlMapping(entry)) {
    errors.push(`${where} must be a mapping.`);
    return null;
  }

  rejectUnknownKeys(entry, WORKFLOW_SCRIPT_KEYS, where, errors);

  const id = yamlString(entry.id);
  if (id.length === 0) {
    errors.push(`${where} needs an 'id'.`);
    return null;
  }

  const trigger = normalizeScriptTrigger(yamlString(entry.trigger));
  if (trigger === null) {
    errors.push(
      `${where} has an unknown trigger "${yamlString(entry.trigger)}" (expected ${SCRIPT_TRIGGERS.join(", ")}).`,
    );
    return null;
  }

  let revert: string | undefined;
  if ("revert" in entry) {
    const normalized = normalizeScriptRevert(yamlString(entry.revert));
    if (normalized === null) {
      errors.push(
        `${where} has an unknown revert "${yamlString(entry.revert)}" (expected ${SCRIPT_REVERTS.join(", ")}).`,
      );
      return null;
    }

    revert = normalized;
  }

  const steps: WorkflowYamlScriptStep[] = [];
  if (Array.isArray(entry.steps)) {
    entry.steps.forEach((raw, entryIndex) => {
      if (typeof raw === "string") {
        steps.push({ say: raw.trim() });
        return;
      }

      if (!isYamlMapping(raw)) {
        errors.push(`${where}.steps[${entryIndex}] must be a mapping.`);
        return;
      }

      const parsed = parseScriptStepMapping(raw, `${where}.steps[${entryIndex}]`, errors);
      if (parsed !== null) steps.push(parsed);
    });
  }

  return { id, name: yamlString(entry.name) || undefined, trigger, revert, steps };
}

function nullableString(value: unknown): string | null {
  return value === null ? null : yamlString(value);
}

// ---------------------------------------------------------------------------
// Sparse patch application (structural merge on the typed model)
// ---------------------------------------------------------------------------

export interface WorkflowPatchApplyResult {
  /** The new document (a clone; the input is never mutated). */
  readonly document: WorkflowDocument;
  /** Human-readable reasons the patch was refused or partially skipped. */
  readonly errors: string[];
  /** True when at least one field actually changed. */
  readonly changed: boolean;
}

/**
 * Applies a (possibly partial) patch onto a document, returning a NEW
 * document. Semantics:
 *  - key present -> applied; key absent -> untouched; explicit null -> cleared.
 *  - step matched by `id` first, then `name`; unmatched -> appended as a new
 *    step (inside its `group` when given, else at the raw end — joining
 *    whatever group is open there, since group runs are positional).
 *  - `scripts` / `triggers` presence REPLACES that step's whole list.
 *  - `group: <name>` moves the step to the end of that group's run (creating
 *    the group when new); `group: null` moves it to the end of the ungrouped
 *    head run.
 */
export function applyWorkflowPatch(
  initial: WorkflowDocument,
  patch: WorkflowYamlPatch,
): WorkflowPatchApplyResult {
  const before = serializeWorkflowDocument(initial);
  const document = cloneWorkflowDocument(initial);
  const errors: string[] = [];

  if (patch.workflow?.name !== undefined) document.workflowName = patch.workflow.name;
  if (patch.workflow?.description !== undefined) {
    document.description = patch.workflow.description;
  }
  if (patch.workflow?.takes_about !== undefined) {
    document.expectedDuration = patch.workflow.takes_about;
  }
  if (patch.workflow?.language !== undefined) document.language = patch.workflow.language;
  if (patch.workflow?.product !== undefined) document.product = patch.workflow.product;
  if (patch.workflow?.user_input_allowed !== undefined) {
    applyPatchUserInputAllowed(document, patch.workflow.user_input_allowed);
  }
  if (patch.workflow?.autostart_desktop !== undefined) {
    document.autostartDesktop = patch.workflow.autostart_desktop;
  }
  if (patch.workflow?.autostart_headset !== undefined) {
    document.autostartHeadset = patch.workflow.autostart_headset;
  }
  if (patch.workflow?.remember_progress !== undefined) {
    document.rememberProgress = patch.workflow.remember_progress;
  }
  if (patch.workflow?.requires_console_placement !== undefined) {
    document.requiresConsolePlacement = patch.workflow.requires_console_placement;
  }
  if (patch.workflow?.lock_ui !== undefined) document.lockUi = patch.workflow.lock_ui;
  if (patch.workflow?.use_custom_ui !== undefined) {
    document.useCustomUi = patch.workflow.use_custom_ui;
  }
  if (patch.workflow?.disable_mobile_keyboard !== undefined) {
    document.disableMobileKeyboard = patch.workflow.disable_mobile_keyboard;
  }

  for (const stepPatch of patch.steps ?? []) {
    applyStepPatch(document, stepPatch, errors);
  }

  rebuildGroupMembership(document);

  const after = serializeWorkflowDocument(document);
  return { document, errors, changed: after !== before };
}

function applyStepPatch(
  document: WorkflowDocument,
  patch: WorkflowYamlStep,
  errors: string[],
): void {
  let step = resolveStep(document, patch);

  if (step === null) {
    if (patch.name.trim().length === 0) {
      errors.push(`A new step needs a 'name' (id "${patch.id ?? ""}" matched nothing).`);
      return;
    }

    step = new WorkflowStep();
    step.name = patch.name.trim();
    if (typeof patch.group === "string" && patch.group.trim().length > 0) {
      step.groupName = patch.group.trim();
      insertStepIntoGroup(document, step, step.groupName);
    } else if (patch.group === null) {
      step.groupName = "";
      insertStepUngrouped(document, step);
    } else {
      // No group key: append at the raw end, joining whatever group is open
      // there (group membership is positional in the authored order).
      const last = document.steps[document.steps.length - 1];
      step.groupName = last ? last.groupName : "";
      document.steps.push(step);
    }
  } else if (patch.group !== undefined) {
    const targetGroup = patch.group === null ? "" : patch.group.trim();
    if (step.groupName.toLowerCase() !== targetGroup.toLowerCase()) {
      removeStep(document, step);
      step.groupName = targetGroup;
      if (targetGroup.length === 0) insertStepUngrouped(document, step);
      else insertStepIntoGroup(document, step, targetGroup);
    }
  }

  // A rename is only possible once the step is addressable by a stable id.
  if (patch.id !== undefined) {
    step.stepId = patch.id;
    if (patch.name.trim().length > 0 && step.name !== patch.name.trim()) {
      const oldName = step.name;
      step.name = patch.name.trim();
      renameReferences(document, oldName, step.name);
    }
  }

  if (patch.disabled !== undefined) step.disabled = patch.disabled;
  if (patch.scene !== undefined) step.scene = patch.scene;
  if (patch.takes_about !== undefined) step.expectedDuration = patch.takes_about;
  if (patch.step_context !== undefined) step.stepContext = patch.step_context;
  if (patch.step_instructions !== undefined) step.stepInstructions = patch.step_instructions;
  if (patch.back_button !== undefined) step.backButton = patch.back_button;
  if (patch.next_button !== undefined) step.nextButton = patch.next_button;
  if (patch.next_target !== undefined) step.nextTarget = normalizeNextTarget(patch.next_target);
  if (patch.is_completion_step !== undefined) step.isCompletionStep = patch.is_completion_step;
  if (patch.completion_condition !== undefined) {
    step.completionCondition = completionConditionFromPatch(patch.completion_condition);
  }
  if (patch.user_input_allowed !== undefined) {
    applyPatchUserInputAllowed(step, patch.user_input_allowed);
  }
  if (patch.attachment !== undefined) {
    if (patch.attachment === null) {
      step.attachment = null;
    } else {
      const attachment = new WorkflowStepAttachment();
      attachment.part = patch.attachment.part ?? "";
      attachment.point = patch.attachment.point ?? null;
      step.attachment = attachment.isEmpty ? null : attachment;
    }
  }

  if (patch.step_view !== undefined) {
    step.stepView = stepViewFromPatch(patch.step_view);
  }

  if (patch.media !== undefined) {
    step.media = mediaFromPatch(patch.media);
  }

  if (patch.triggers !== undefined) {
    step.triggers = (patch.triggers ?? []).map((entry) => {
      const trigger = new WorkflowStepTrigger();
      trigger.type = normalizeStepTriggerType(entry.type) ?? "button";
      trigger.label = entry.label;
      trigger.goTo = entry.go_to;
      return trigger;
    });
  }

  if (patch.scripts !== undefined) {
    step.scripts = patch.scripts.map((entry) => patchScriptToModel(entry));
  }
}

/**
 * Shared by the patch merge and the create-from-scratch path, so a condition
 * written into a brand-new document and one patched onto an existing step
 * cannot be built two different ways.
 */
function completionConditionFromPatch(
  entry: WorkflowYamlCompletionCondition | null | undefined,
): WorkflowStepCompletionCondition | null {
  if (entry === null || entry === undefined) return null;
  const condition = new WorkflowStepCompletionCondition();
  condition.type = entry.type ?? null;
  condition.delaySeconds = entry.delay_seconds ?? null;
  return condition.isEmpty ? null : condition;
}

/**
 * A patch's `step_view` as the model's block. Presence REPLACES, so an omitted
 * half is CLEARED rather than inherited: a pose is one thing, and a camera sent
 * to the previous framing's pivot from a new position looks at the wrong point.
 * `validate()` warns about the half that results, and `resolveStepView` fails
 * open so the step simply does not move the camera.
 */
function stepViewFromPatch(
  entry: WorkflowYamlStepView | null | undefined,
): WorkflowStepView | null {
  if (entry === null || entry === undefined) return null;
  const view = new WorkflowStepView();
  view.position = entry.position ?? null;
  view.pivot = entry.pivot ?? null;
  return view.isEmpty ? null : view;
}

/**
 * A patch's `media` as the model's block. Shared by the patch-an-existing-step
 * and the create-from-scratch paths — the create path used to have no media leg
 * at all, so the same patch kept its media on an existing document and lost it
 * on a new one.
 */
function mediaFromPatch(entry: WorkflowYamlMedia | null | undefined): WorkflowStepMedia | null {
  if (entry === null || entry === undefined) return null;
  const media = new WorkflowStepMedia();
  media.type = entry.type ?? null;
  media.fit = entry.fit ?? null;
  media.url = entry.url ?? "";
  media.name = entry.name ?? "";
  media.description = entry.description ?? "";
  media.transcript = entry.transcript ?? "";
  media.startTime = entry.start ?? "";
  media.endTime = entry.end ?? "";
  // `??`, so an explicit `loop: false` survives as false rather than being
  // read as "not stated" the way a falsy `||` would.
  media.loop = entry.loop ?? null;
  media.autoplay = entry.autoplay ?? null;
  return media.isEmpty ? null : media;
}

function patchScriptToModel(entry: WorkflowYamlScript): WorkflowScript {
  const script = new WorkflowScript();
  script.id = entry.id;
  script.name = entry.name ?? "";
  script.trigger = normalizeScriptTrigger(entry.trigger) ?? "manual";
  script.revert = normalizeScriptRevert(entry.revert ?? "none") ?? "none";
  script.steps = entry.steps.map((raw) => {
    const step = new WorkflowScriptStep();
    if (raw.say !== undefined && raw.say.length > 0) {
      step.sayText = raw.say;
      return step;
    }

    step.tool = raw.tool ?? "";
    step.args = raw.args ?? null;
    return step;
  });

  return script;
}

/** Resolves a patch step against the document: stable id first, then name. */
function resolveStep(document: WorkflowDocument, step: WorkflowYamlStep): WorkflowStep | null {
  if (step.id !== undefined && step.id.length > 0) {
    const byId = document.findStepById(step.id);
    if (byId) return byId;
  }

  return document.findStep(step.name);
}

function removeStep(document: WorkflowDocument, step: WorkflowStep): void {
  const index = document.steps.indexOf(step);
  if (index >= 0) document.steps.splice(index, 1);
}

/** Inserts after the last step of the group's run, or at the end when new. */
function insertStepIntoGroup(
  document: WorkflowDocument,
  step: WorkflowStep,
  groupName: string,
): void {
  let insertAt = -1;
  for (let i = 0; i < document.steps.length; i++) {
    if (document.steps[i].groupName.toLowerCase() === groupName.toLowerCase()) {
      insertAt = i + 1;
    }
  }

  if (insertAt < 0) {
    // New (or empty) group: the step opens the group's run at the end.
    document.steps.push(step);
    if (document.findGroup(groupName) === null) {
      const group = new WorkflowGroup();
      group.name = groupName;
      document.groups.push(group);
    }
    return;
  }

  document.steps.splice(insertAt, 0, step);
}

/** Inserts at the end of the ungrouped head run (before the first grouped step). */
function insertStepUngrouped(document: WorkflowDocument, step: WorkflowStep): void {
  let insertAt = 0;
  while (
    insertAt < document.steps.length &&
    document.steps[insertAt].groupName.length === 0
  ) {
    insertAt++;
  }

  document.steps.splice(insertAt, 0, step);
}

/**
 * A rename keeps name-keyed references coherent: run state and completed
 * lists re-validate themselves against step names elsewhere, but trigger
 * `go_to` targets, `next_target` targets and handoffs naming the old name would dangle. Only exact
 * (case-insensitive) matches are rewritten.
 */
function renameReferences(document: WorkflowDocument, oldName: string, newName: string): void {
  for (const step of document.steps) {
    for (const trigger of step.triggers) {
      if (trigger.goTo.toLowerCase() === oldName.toLowerCase()) trigger.goTo = newName;
    }
    if (step.nextTarget !== null && step.nextTarget.toLowerCase() === oldName.toLowerCase()) {
      step.nextTarget = newName;
    }
  }
}

// ---------------------------------------------------------------------------
// Create-from-scratch
// ---------------------------------------------------------------------------

/**
 * Builds a complete document from a patch, for a project that has no workflow
 * yet. The caller stamps identity (uid / project) afterwards, exactly
 * like the template path. An explicit `group: null` means UNGROUPED, so those
 * steps are hoisted into the head run; absent-group steps keep their
 * position (before any group they are head steps; after one they ride the
 * open group — positional semantics).
 */
export function documentFromPatch(patch: WorkflowYamlPatch): WorkflowDocument {
  const document = new WorkflowDocument();
  document.workflowName = patch.workflow?.name?.trim() || "";
  document.description = patch.workflow?.description ?? null;
  document.expectedDuration = patch.workflow?.takes_about ?? null;
  document.language = patch.workflow?.language ?? null;
  document.product = patch.workflow?.product ?? null;
  applyPatchUserInputAllowed(document, patch.workflow?.user_input_allowed ?? null);
  document.autostartDesktop = patch.workflow?.autostart_desktop ?? null;
  document.autostartHeadset = patch.workflow?.autostart_headset ?? null;
  document.rememberProgress = patch.workflow?.remember_progress ?? null;
  document.requiresConsolePlacement = patch.workflow?.requires_console_placement ?? null;
  document.lockUi = patch.workflow?.lock_ui ?? null;
  document.useCustomUi = patch.workflow?.use_custom_ui ?? null;
  document.disableMobileKeyboard = patch.workflow?.disable_mobile_keyboard ?? null;

  const headSteps: WorkflowYamlStep[] = [];
  const groupedTail: WorkflowYamlStep[] = [];
  let sawExplicitGroup = false;
  let openGroup = "";
  for (const step of patch.steps ?? []) {
    if (typeof step.group === "string" && step.group.trim().length > 0) {
      sawExplicitGroup = true;
      openGroup = step.group.trim();
      groupedTail.push(step);
    } else if (step.group === null || !sawExplicitGroup) {
      headSteps.push(step);
    } else {
      // Absent group after an explicit one: ride the open group.
      groupedTail.push({ ...step, group: openGroup });
    }
  }

  for (const entry of [...headSteps, ...groupedTail]) {
    const step = new WorkflowStep();
    step.name = entry.name.trim();
    step.stepId = entry.id ?? "";
    step.disabled = entry.disabled ?? null;
    step.groupName = typeof entry.group === "string" ? entry.group.trim() : "";
    step.scene = entry.scene ?? null;
    step.expectedDuration = entry.takes_about ?? null;
    step.stepContext = entry.step_context ?? null;
    step.stepInstructions = entry.step_instructions ?? null;
    step.backButton = entry.back_button ?? null;
    step.nextButton = entry.next_button ?? null;
    step.nextTarget = normalizeNextTarget(entry.next_target);
    step.isCompletionStep = entry.is_completion_step ?? null;
    step.completionCondition = completionConditionFromPatch(entry.completion_condition);
    applyPatchUserInputAllowed(step, entry.user_input_allowed ?? null);
    if (entry.attachment) {
      const attachment = new WorkflowStepAttachment();
      attachment.part = entry.attachment.part ?? "";
      attachment.point = entry.attachment.point ?? null;
      step.attachment = attachment.isEmpty ? null : attachment;
    }
    step.stepView = stepViewFromPatch(entry.step_view);
    // `media` was missing here — a create-from-scratch patch (the branch a
    // project with no workflow yet takes) silently dropped every step's media
    // while the same patch applied to an EXISTING document kept it. Fixed in
    // passing while adding the sibling above.
    step.media = mediaFromPatch(entry.media);
    step.triggers = (entry.triggers ?? []).map((trigger) => {
      const model = new WorkflowStepTrigger();
      model.type = normalizeStepTriggerType(trigger.type) ?? "button";
      model.label = trigger.label;
      model.goTo = trigger.go_to;
      return model;
    });
    step.scripts = (entry.scripts ?? []).map((script) => patchScriptToModel(script));
    document.steps.push(step);
  }

  rebuildGroupMembership(document);
  return document;
}

// ---------------------------------------------------------------------------
// YAML-text line ranges (raw dev-mode highlighting)
// ---------------------------------------------------------------------------

/** A half-open 1-based line range inside the serialized YAML text. */
export interface WorkflowYamlLineRange {
  readonly startLine: number;
  readonly endLineExclusive: number;
}

/**
 * Scans the CANONICAL serialized YAML for each step's line range (1-based,
 * end-exclusive) keyed by step name, plus the header (`workflow:`) range.
 * Works only on this module's own serializer output — top-level keys at
 * column 0, one `- ` entry per step under `steps:` — which is exactly the
 * text the raw dev mode displays.
 */
export function computeYamlSectionRanges(yamlText: string): {
  header: WorkflowYamlLineRange | null;
  steps: Map<string, WorkflowYamlLineRange>;
} {
  const lines = yamlText.split("\n");
  const steps = new Map<string, WorkflowYamlLineRange>();
  let header: WorkflowYamlLineRange | null = null;

  let inSteps = false;
  let headerStart = -1;
  let currentStepStart = -1;
  let currentStepName = "";

  const closeStep = (endExclusive: number) => {
    if (currentStepStart >= 0 && currentStepName.length > 0) {
      steps.set(currentStepName.toLowerCase(), {
        startLine: currentStepStart + 1,
        endLineExclusive: endExclusive + 1,
      });
    }
    currentStepStart = -1;
    currentStepName = "";
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const isTopLevelKey = /^[A-Za-z_][\w-]*:/.test(line);

    if (isTopLevelKey) {
      if (headerStart >= 0 && header === null) {
        header = { startLine: headerStart + 1, endLineExclusive: i + 1 };
        headerStart = -1;
      }
      closeStep(i);
      inSteps = line.startsWith("steps:");
      if (line.startsWith("workflow:")) headerStart = i;
      continue;
    }

    if (inSteps && /^ {0,2}- /.test(line)) {
      closeStep(i);
      currentStepStart = i;
    }

    if (currentStepStart >= 0 && currentStepName.length === 0) {
      const nameMatch = /^\s*(?:- )?name:\s*(.+)$/.exec(line);
      if (nameMatch) {
        currentStepName = stripYamlScalarQuotes(nameMatch[1].trim());
      }
    }
  }

  if (headerStart >= 0 && header === null) {
    header = { startLine: headerStart + 1, endLineExclusive: lines.length + 1 };
  }
  closeStep(lines.length);

  return { header, steps };
}

function stripYamlScalarQuotes(value: string): string {
  if (
    value.length >= 2 &&
    ((value.startsWith('"') && value.endsWith('"')) ||
      (value.startsWith("'") && value.endsWith("'")))
  ) {
    return value.slice(1, -1);
  }

  return value;
}
